/*
  AMAVO CLINICAL — collection and search listing
  ------------------------------------------------------------------
  Two enhancements over markup that already works:
    - filters apply on change instead of on submit
    - "load more" appends instead of navigating

  Both keep the URL in step with what is on screen, so the back button
  and a shared link land on the same result set.
*/

(() => {
  'use strict';

  const SECTION_PARAM = 'section_id';
  const TAG_PARAM = 'amv_tag';
  let renderVersion = 0;

  function taxonomyTags() {
    return new URLSearchParams(window.location.search).getAll(TAG_PARAM);
  }

  async function fetchSection(url, sectionId) {
    const target = new URL(url, window.location.origin);
    target.searchParams.set(SECTION_PARAM, sectionId);
    const res = await fetch(target.toString());
    if (!res.ok) throw new Error(res.statusText);
    const html = await res.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /* ---------------------------------------------------------------
     FACETS
     --------------------------------------------------------------- */

  class AmvFacets extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[id^="shopify-section-"]');
      if (!this.section) return;
      this.sectionId = this.section.id.replace('shopify-section-', '');

      this.forms = Array.from(this.section.querySelectorAll('[data-amv-facets]'));
      this.forms.forEach((form) => {
        form.setAttribute('data-live', '');
        form.addEventListener('change', () => this.apply(form));
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.apply(form);
        });
      });
    }

    buildUrl(form) {
      const data = new FormData(form);
      const params = new URLSearchParams();
      for (const [key, value] of data.entries()) {
        if (value !== '') params.append(key, value);
      }
      // Filtrele de taxonomie traiesc doar in pagina; le ducem mai departe
      // in adresa, ca sa supravietuiasca inlocuirii sectiunii.
      taxonomyTags().forEach((tag) => params.append(TAG_PARAM, tag));
      const base = window.location.pathname;
      const qs = params.toString();
      return qs ? `${base}?${qs}` : base;
    }

    apply(form) {
      return this.render(this.buildUrl(form), true);
    }

    /* Un singur raspuns castiga: cel al ultimei cereri. Unul lent, sosit
       dupa altul mai nou, ar fi pus pe ecran filtrele vechi. Adresa se
       schimba inaintea continutului, ca taxonomia re-randata s-o citeasca. */
    async render(url, push) {
      const version = ++renderVersion;
      this.section.setAttribute('aria-busy', 'true');
      try {
        const doc = await fetchSection(url, this.sectionId);
        if (version !== renderVersion) return;
        const fresh = doc.getElementById(`shopify-section-${this.sectionId}`) ||
                      doc.querySelector('#amv-results');
        // pushState, not replaceState: filtering is navigation, and the
        // back button should undo it.
        if (push) window.history.pushState({ amvFacets: true }, '', url);
        if (fresh) {
          const current = document.getElementById(`shopify-section-${this.sectionId}`) ||
                          document.getElementById('amv-results');
          if (current) current.innerHTML = fresh.innerHTML;
        }
      } catch (e) {
        if (version === renderVersion) window.location.href = url;
      } finally {
        if (version === renderVersion) this.section.removeAttribute('aria-busy');
      }
    }
  }

  /* Inapoi/Inainte: lista se reface din adresa la care s-a ajuns. */
  window.addEventListener('popstate', () => {
    const facets = document.querySelector('amv-facets');
    if (facets && facets.section) facets.render(window.location.href, false);
  });

  /* ---------------------------------------------------------------
     LOAD MORE
     The button stays a link to the next page, so it works without this
     script and every page keeps a crawlable URL.
     --------------------------------------------------------------- */

  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-amv-load-more]');
    if (!btn) return;

    const section = btn.closest('[id^="shopify-section-"]');
    if (!section) return;

    event.preventDefault();
    const url = btn.getAttribute('href');
    btn.setAttribute('aria-busy', 'true');

    try {
      const doc = await fetchSection(url, section.id.replace('shopify-section-', ''));
      const grid = section.querySelector('[data-amv-grid]');
      const freshGrid = doc.querySelector('[data-amv-grid]');
      const freshPager = doc.querySelector('.amv-pager');

      if (grid && freshGrid) {
        Array.from(freshGrid.children).forEach((li) => grid.appendChild(li));
      }

      const pager = section.querySelector('.amv-pager');
      if (pager) {
        if (freshPager) pager.replaceWith(freshPager);
        else pager.remove();
      }

      window.history.replaceState({}, '', url);

      // Move focus to the first appended card so keyboard users are not
      // dropped back at the top of the list.
      if (grid) {
        const first = grid.children[grid.children.length - 1];
        const link = first && first.querySelector('a');
        if (link) link.focus({ preventScroll: true });
      }
    } catch (e) {
      window.location.href = url;
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });

  if (!customElements.get('amv-facets')) customElements.define('amv-facets', AmvFacets);

  class AmvTaxonomyFacets extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[id^="shopify-section-"]');
      if (!this.section) return;
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-amv-taxonomy-filter]');
        if (!input) return;
        document.querySelectorAll(`[data-amv-taxonomy-filter][data-tag="${input.dataset.tag}"]`)
          .forEach((control) => { control.checked = input.checked; });
        this.persist();
        this.apply();
      });
      this.addEventListener('click', (event) => {
        if (!event.target.closest('[data-amv-taxonomy-reset]')) return;
        document.querySelectorAll('[data-amv-taxonomy-filter]').forEach((control) => { control.checked = false; });
        this.persist();
        this.apply();
      });
      // Selectia vine din adresa: link trimis, reincarcare, Inapoi.
      const wanted = new Set(taxonomyTags());
      this.querySelectorAll('[data-amv-taxonomy-filter]').forEach((input) => { input.checked = wanted.has(input.dataset.tag); });
      this.apply();
    }

    persist() {
      const url = new URL(window.location.href);
      url.searchParams.delete(TAG_PARAM);
      const tags = new Set();
      this.selected().forEach((set) => set.forEach((tag) => tags.add(tag)));
      tags.forEach((tag) => url.searchParams.append(TAG_PARAM, tag));
      window.history.pushState({ amvFacets: true }, '', url);
    }

    selected() {
      const byAxis = new Map();
      document.querySelectorAll('[data-amv-taxonomy-filter]:checked').forEach((input) => {
        const axis = input.dataset.axis;
        if (!byAxis.has(axis)) byAxis.set(axis, new Set());
        byAxis.get(axis).add(input.dataset.tag);
      });
      return byAxis;
    }

    matches(tags, selected) {
      return Array.from(selected.values()).every((wanted) => Array.from(wanted).some((tag) => tags.includes(tag)));
    }

    apply() {
      const selected = this.selected();
      const products = Array.from(this.section.querySelectorAll('[data-amv-taxonomy-product]'));
      let shown = 0;
      products.forEach((product) => {
        const tags = (product.dataset.amvTaxonomyTags || '').split('|').filter(Boolean);
        const match = this.matches(tags, selected);
        product.hidden = !match;
        if (match) shown += 1;
      });

      this.section.querySelectorAll('[data-amv-taxonomy-count]').forEach((out) => {
        const tag = out.dataset.amvTaxonomyCount;
        const count = products.filter((product) => {
          const tags = (product.dataset.amvTaxonomyTags || '').split('|').filter(Boolean);
          return this.matches(tags, selected) && tags.includes(tag);
        }).length;
        out.textContent = count;
      });

      this.section.querySelectorAll('[data-amv-taxonomy-reset]').forEach((button) => { button.hidden = selected.size === 0; });
      const empty = this.section.querySelector('[data-amv-taxonomy-empty]');
      if (empty) empty.hidden = shown > 0;
      const grid = this.section.querySelector('[data-amv-grid]');
      if (grid) grid.hidden = shown === 0;

      /*
        Numaratoarea se intoarce la textul randat de Liquid cand nu mai e
        niciun filtru bifat. Inainte se scria doar cand exista o selectie,
        deci dupa „Resetează" ramaneau pe ecran cele 8 produse ale ultimului
        filtru peste o lista din care se vedeau iar toate 30.

        Formele de plural vin din locale, nu din script: „produs/produse" scris
        aici ar fi ramas in romana si pe un magazin in alta limba.
      */
      const count = this.section.querySelector('.amv-listing__count');
      if (!count) return;
      if (count.dataset.amvCountTotal === undefined) count.dataset.amvCountTotal = count.textContent.trim();
      if (selected.size === 0) {
        count.textContent = count.dataset.amvCountTotal;
        return;
      }
      const sablon = shown === 1 ? count.dataset.amvCountOne : count.dataset.amvCountMany;
      count.textContent = sablon ? sablon.replace(/\d+/, shown) : String(shown);
    }
  }

  if (!customElements.get('amv-taxonomy-facets')) customElements.define('amv-taxonomy-facets', AmvTaxonomyFacets);
})();
